
import {
  Component,
  Mixins,
  Watch,
  Provide,
  Prop
} from "vue-property-decorator";
import { mapState } from "vuex";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import {
  IDocsSearchProviderData,
  IDocsSearchProviderMethods
} from "@/models/providers/docs";
import { IDocsCategory } from "@/models/docs";

@Component({
  computed: {
    ...mapState({
      docsSearchScope(state: IState, getters) {
        return getters[`data/${DataModules.DOCUMENTATION}/docsSearchScope`];
      },
      searchDocs(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.DOCUMENTATION,
          scope: this.docsSearchScope
        });
      }
    })
  }
})
export default class DocsSearchProvider extends Mixins(RequestPromises) {
  // Props
  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: false })
  public handleRequestError!: boolean;

  @Prop({ default: false })
  public docsSearchQuery!: string;

  // Map State

  public docsSearchScope!: string;
  public searchDocs!: IDocsCategory[];

  // Variables

  public queryText = "";
  public debounceSearch = this.$_.debounce(this.search, 250);

  // Data

  public isLoading = false;
  public isReloading = false;
  public hasRequestError = false;

  // Created

  public created() {
    if (this.docsSearchQuery != "" && !this.$_.isNull(this.docsSearchQuery)) {
      this.queryText = this.docsSearchQuery;
      this.search();
    }
  }

  // Lifecycle methods

  public beforeDestroy() {
    this.destroySearchResults();
  }

  // Methods

  public reloadData() {
    this.isReloading = true;
    return this.search();
  }

  public destroySearchResults() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.DOCUMENTATION,
      scope: this.docsSearchScope,
      vm: this
    });
  }

  public async search() {
    if (this.queryText === "") {
      this.destroySearchResults();
      return;
    }

    try {
      await this.$store.dispatch(
        `data/${DataModules.DOCUMENTATION}/searchDocuments`,
        {
          queryText: this.queryText,
          scope: this.docsSearchScope,
          vm: this
        }
      );
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public updateQueryText(value = "") {
    this.queryText = _.trim(value);
    this.isLoading = true;

    if (this.queryText === "") {
      this.destroySearchResults();
      this.isLoading = false;
      return;
    }

    this.debounceSearch();
  }

  // Watchers

  @Watch("queryText", { immediate: true })
  public onQueryTextChange() {
    this.$emit("onSearch", !!this.queryText);
  }

  // Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $docsSearchData: this.providerData(),
        $docsSearchMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("docsSearchProviderData")
  public providerData(): IDocsSearchProviderData {
    return {
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError,
      queryText: this.queryText,
      searchDocs: this.searchDocs
    };
  }

  // Provide Methods
  @Provide("docsSearchProviderMethods")
  public providerMethods: IDocsSearchProviderMethods = {
    reloadData: this.reloadData,
    updateQueryText: this.updateQueryText
  };
}
